import React, { useEffect, useState } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon, RotateCw, Trash2Icon, CheckIcon, XIcon } from "lucide-react";
import { PaginationData, User } from "../../../../types";
import {
  getUsersForPaginated,
  getUnverifiedUsers,
  deleteUnverifiedUser,
} from "../../../../service/apiService";

const UsersList = ({}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>({
    page: 1,
    items_per_page: 10,
    total_pages: 1,
    has_next: false,
    next_page: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Unverified users (Cognito FORCE_CHANGE_PASSWORD) — invited, never signed in.
  const [unverifiedIds, setUnverifiedIds] = useState<Set<string>>(new Set());
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchUsers = async (page: number, itemsPerPage: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getUsersForPaginated(page, itemsPerPage);
      setUsers(result.users);
      setPagination(result.pagination);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to fetch users. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUnverified = async () => {
    try {
      const result = await getUnverifiedUsers();
      setUnverifiedIds(new Set(result.map((u) => u.id.toLowerCase())));
    } catch (error) {
      // Non-blocking: the list still renders without unverified labels.
      console.error("Error fetching unverified users:", error);
    }
  };

  useEffect(() => {
    if (pagination) {
      fetchUsers(pagination.page, pagination.items_per_page);
    }
  }, [pagination?.page, pagination?.items_per_page]);

  useEffect(() => {
    fetchUnverified();
  }, []);

  const isUnverified = (user: User) =>
    unverifiedIds.has(user.id.toLowerCase());

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    const target = userToDelete;
    setUserToDelete(null);
    setDeletingUserId(target);
    setDeleteSuccess(null);
    setDeleteError(null);

    try {
      const result = await deleteUnverifiedUser(target.toLowerCase());

      if (result && !result.error) {
        setDeleteSuccess(target);
        await fetchUnverified();
        if (pagination) {
          await fetchUsers(pagination.page, pagination.items_per_page);
        }
      } else {
        setDeleteError(
          result?.error || "Failed to delete user. Please try again."
        );
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 409) {
        setDeleteError(
          detail ||
            "This user has already signed in and can no longer be deleted here."
        );
        await fetchUnverified();
      } else if (status === 404) {
        setDeleteError("User not found. The list may be out of date.");
        await fetchUnverified();
        if (pagination) {
          await fetchUsers(pagination.page, pagination.items_per_page);
        }
      } else if (status === 403) {
        setDeleteError("You don't have permission to delete users.");
      } else if (status === 400) {
        setDeleteError(detail || "You cannot delete your own account.");
      } else {
        setDeleteError(
          detail || err?.message || "Failed to delete user. Please try again."
        );
      }
    } finally {
      setDeletingUserId(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    // Add validation to prevent going beyond total pages
    if (pagination && (newPage >=1 && newPage <= pagination.total_pages)) {
      setPagination((prev) => {
        if (prev === null) {
          return {
            page: 1,
            items_per_page: 10,
            total_pages: 1,
            has_next: false,
            next_page: null,
          };
        }
        return {
          ...prev,
          page: newPage,
          has_next: newPage < prev.total_pages,
          next_page: newPage < prev.total_pages ? newPage + 1 : null
        };
      });
    }
  };

  return (
    <Disclosure as="div" className="py-2">
      {({ open }) => (
        <>
          <DisclosureButton className="group flex w-full items-center justify-between">
            <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
              Users List
            </span>
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 group-hover:text-gray-300 transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            />
          </DisclosureButton>
          <DisclosurePanel className="mt-4 text-sm text-gray-600">
            {pagination && (
              <div className="mb-4 flex justify-between items-center">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                    pagination.page <= 1
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-violet-600 hover:bg-violet-700"
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                  className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                    pagination.page >= pagination.total_pages
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-violet-600 hover:bg-violet-700"
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
                >
                  Next
                </button>
              </div>
            )}

            {/* Unverified delete feedback */}
            {deleteSuccess && (
              <div className="mb-2 flex items-center">
                <CheckIcon className="h-5 w-5 text-green-500" />
                <span className="ml-2 text-sm text-green-500">
                  {deleteSuccess} permanently deleted. You can send a new
                  invitation from Create User.
                </span>
              </div>
            )}
            {deleteError && (
              <div className="mb-2 flex items-center">
                <XIcon className="h-5 w-5 text-violet-500" />
                <span className="ml-2 text-sm text-violet-500">
                  {deleteError}
                </span>
              </div>
            )}

            <div className="mt-6 overflow-auto rounded-lg border border-gray-200 max-h-[60vh]">
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
                  <span>Loading...</span>
                </div>
              ) : error ? (
                <div>Error: {error}</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-20">
                    <tr>
                      <th className="sticky left-0 z-30 bg-gray-100 border-r border-gray-200 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned Knowledge Bases
                      </th> */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Updated At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        File Permissions
                      </th>
                      {/* ──✅ ADDED: Status column header── */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.length > 0 ? (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td className="sticky left-0 z-10 bg-white border-r border-gray-200 px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.id}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {user.role}
                          </td>
                          {/* TODO Need to handle this properly */}
                          {/* <td className="px-6 py-4 text-sm text-gray-900">
                            {Array.isArray(user.assigned_knowledge_base) &&
                            user.assigned_knowledge_base.length > 0
                              ? user.assigned_knowledge_base.join(", ")
                              : "-"}
                          </td> */}
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {user.updated_at}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {user.created_at}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {user.last_login}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">Download:</span>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  user.permissions?.files?.canDownload !== false
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {user.permissions?.files?.canDownload !== false ? 'Yes' : 'No'}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">Preview:</span>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  user.permissions?.files?.canPreview !== false
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {user.permissions?.files?.canPreview !== false ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </div>
                          </td>{/* ── ✅ ADDED: Status column cell ── */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5
                                rounded-full text-xs font-medium ${
                                  user.user_status === "archived"
                                    ? "bg-gray-100 text-gray-700"
                                    : user.user_status === "disabled"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                            >
                              {user.user_status ?? "enabled"}
                            </span>
                            {isUnverified(user) && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                unverified
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {isUnverified(user) ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setDeleteSuccess(null);
                                  setDeleteError(null);
                                  setUserToDelete(user.id);
                                }}
                                disabled={deletingUserId !== null}
                                title="Delete this unverified user so they can be re-invited"
                                className={`inline-flex items-center rounded-md px-2.5 py-1.5
                                  text-xs font-medium text-white
                                  ${
                                    deletingUserId !== null
                                      ? "bg-gray-400 cursor-not-allowed"
                                      : "bg-red-500 hover:bg-red-600"
                                  } focus:outline-none focus:ring-2
                                  focus:ring-offset-2 focus:ring-red-500`}
                              >
                                {deletingUserId === user.id ? (
                                  <>
                                    <RotateCw
                                      className="animate-spin mr-1.5 h-3.5 w-3.5 text-white"
                                      aria-hidden="true"
                                    />
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <Trash2Icon
                                      className="mr-1.5 h-3.5 w-3.5"
                                      aria-hidden="true"
                                    />
                                    Delete
                                  </>
                                )}
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-6 py-4 text-center text-sm text-gray-500"
                        >
                          No users available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* ── Delete Unverified User Confirmation Modal ─────────────────── */}
            {userToDelete && (
              <div className="fixed inset-0 z-50 flex items-center justify-center scrim px-4">
                <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                  <h3 className="text-center text-lg font-semibold text-gray-900">
                    Delete Unverified User?
                  </h3>
                  <p className="mt-2 text-center text-sm text-gray-600">
                    Are you sure you want to permanently delete{" "}
                    <strong className="text-gray-900">{userToDelete}</strong>?
                    <br />
                    <span className="text-gray-500 text-xs mt-1 block">
                      This removes the user from both the database and Cognito
                      and cannot be undone. The user never signed in, so no
                      sessions or data will be lost. You can send them a new
                      invitation from Create User afterwards.
                    </span>
                  </p>
                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={() => setUserToDelete(null)}
                      className="flex-1 rounded-md border border-gray-300 bg-white
                        px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50
                        focus:outline-none focus:ring-2 focus:ring-offset-2
                        focus:ring-violet-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmDelete}
                      className="flex-1 rounded-md px-4 py-2 text-sm font-medium
                        text-white bg-red-500 hover:bg-red-600
                        focus:outline-none focus:ring-2 focus:ring-offset-2
                        focus:ring-red-500 transition-colors"
                    >
                      Yes, Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
};

export default UsersList;
